<template>
  <ontos-view-tabs v-if="hasKanban" :views="views" :model-value="current"/>
</template>

<script lang="ts">
import {Component, Vue, Prop, Watch, toNative} from 'vue-facing-decorator'
import Idea from '@/types/Idea'
import storage from '@/storage'
import {hasDimension} from '@/utils/kanbanDimensions'
import ViewTabs, {ViewTab} from '@/components/shared/ViewTabs.vue'

//`as const` para o valor de cada visão continuar sendo literal: ele é também a chave de
//tradução, e o $tt é tipado pelas chaves de en.json.
const VIEWS = [
  {value: 'list', route: 'list-instances', icon: 'mdi-view-list'},
  {value: 'kanban', route: 'kanban', icon: 'mdi-view-dashboard-outline'}
] as const

/**
 * Lista ou Kanban: as duas visões da mesma coleção.
 *
 * Um componente só para as duas telas — antes cada uma trazia o botão que levava à
 * outra, e as duas metades foram escritas em lugares diferentes com rótulos diferentes.
 *
 * Aqui ficam as decisões da COLEÇÃO: o portão, o destino de cada aba e a memória. A faixa em si
 * é o `ontos-view-tabs`, compartilhado com o bloco de relacionamento da ficha, que alterna as
 * mesmas duas visões sem navegar.
 *
 * Ficam na mesma linha do título e à esquerda dos botões, num bloco separado por um
 * traço. Entre elas e as abas da barra fica o caminho, que é de outra natureza e desfaz
 * qualquer eco.
 *
 * Só aparece quando a coleção tem por onde agrupar: sem dimensão não existe Kanban, e uma aba
 * sozinha seria enfeite. É o mesmo portão do quadro, pelo mesmo `hasDimension` — e por isso uma
 * coleção com um atributo de seleção e sem fluxo nenhum passou a ter as duas abas.
 */
@Component({
  name: 'ontos-collection-view-tabs',
  components: {'ontos-view-tabs': ViewTabs}
})
class CollectionViewTabs extends Vue {
  @Prop({required: true}) readonly idea!: Idea | undefined
  /**
   * As coleções do ambiente, que o portão precisa: se um relacionamento serve de dimensão
   * depende da chave da coleção de destino — ver o `hasDimension`.
   */
  @Prop({type: Array as () => Idea[], default: () => []}) readonly ideas!: Idea[]

  /** Cada visão é um destino: trocar de visão aqui é navegar, e o router é quem acende a aba. */
  get views(): ViewTab[] {
    return VIEWS.map(view => ({
      value: view.value,
      icon: view.icon,
      title: this.$tt(view.value) as string,
      to: {name: view.route, params: this.$route.params}
    }))
  }

  /** O mesmo portão do quadro: tem fluxo, ou tem propriedade que sirva de dimensão. */
  get hasKanban(): boolean {
    return !!this.idea && hasDimension(this.idea, this.ideas)
  }

  get current(): string {
    return this.$route.name === 'kanban' ? 'kanban' : 'list'
  }

  mounted() {
    this.remember()
  }

  /**
   * E a cada troca de visão ou de coleção. Na faixa do caminho (no App) este componente NÃO é
   * recriado na navegação — ele vive no casco, fora do router-view —, e só o `mounted` gravava a
   * visão com que a coleção abriu: trocar para a Lista não ficava lembrado, e a coleção voltava a
   * abrir no Kanban (relato do usuário).
   */
  get rememberedKey(): string {
    return `${this.$route.params.system}/${this.$route.params.idea}/${this.current}`
  }

  @Watch('rememberedKey')
  onViewChange() {
    this.remember()
  }

  /**
   * Guarda a visão em que se está — é o que a URL da coleção consulta para saber onde
   * abrir da próxima vez.
   *
   * Ao montar e ao trocar, e não no clique da aba: assim vale também para quem chega pela URL
   * direta, pelo caminho ou por um link salvo.
   */
  remember() {
    const system = this.$route.params.system as string
    const idea = this.$route.params.idea as string
    //Pela rota, e não pelo `current`: na passagem pela entrada da coleção (a URL sem visão, que
    //decide para onde mandar), o `current` diz "lista" por não ser o quadro, e gravaria a lista por
    //cima da lembrança de verdade da coleção nova
    const view = VIEWS.find(candidate => candidate.route === this.$route.name)
    if (system && idea && view) storage.setCollectionView(system, idea, view.route)
  }
}

export default toNative(CollectionViewTabs)
</script>
